import React, { useCallback, useEffect, useState } from "react";
import { addNotification as notify } from "reapop";
import { connect } from "react-redux";
import AdminNavigation from "../AdminNavigation";
import { TETProductManagementFields } from "./Field";
import { validateField } from "../../../pages/functions";
import AdminConfirmationModal from "../adminConfirmationModal";
import Select from "../../../forms/Select";
import InputGroup from "../../../forms/InputGroup";
import { api_v2 } from "../../NewIndex.api.js";
import "./Styles.css";
import { REF_DATA } from "../../../constants.js";
import { getApplicationRegex } from "../../plugins/utils.js";

const TETProductManagement = (props) => {
  const [fields, setFields] = useState(TETProductManagementFields);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedProductDetails, setSelectedProductDetails] = useState({});
  const [showInTheList, setShowInTheList] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [showAdminConfirmationModal, setShowAdminConfirmationModal] =
    useState(false);

  const resetState = useCallback(() => {
    let fieldsValue = fields;
    fieldsValue.country.value = "";
    fieldsValue.ProductName.value = "";
    fieldsValue.ShowInList.value = "";
    fieldsValue.TETType.value = "";
    fieldsValue.TETProductName.value = "";
    setShowAdminConfirmationModal(false);
    setFields(fieldsValue);
    setDisableSubmit(true);
    setSelectedProductDetails({});
    setSelectedProductName("");
    setProductList([]);
  });

  const getCountriesList = useCallback(() => {
    const countryOptions = [];
    api_v2.fetchTETCountries((JsonKeys) => {
      JsonKeys.forEach((countryList) => {
        countryOptions.push({
          value: countryList.country,
          label: countryList.country,
        });
      });
      setCountryList(JsonKeys);
      setCountryOptions(countryOptions);
    });
  }, []);

  useEffect(() => {
    resetState();
    if (countryOptions.length === 0) {
      getCountriesList();
    }
  }, []);

  const validate = useCallback((e, field) => {
    const inputValue = e.target.value.toLowerCase();
    const ref_Data = props.referenceData.find(function (category) {
      return category.type === REF_DATA;
    }).referenceData;
    const regex = getApplicationRegex(ref_Data);

    if (regex.Emoji_Regex !== null && regex.Emoji_Regex.test(inputValue)) {
      // Emoji detected, prevent it from being added
      showNotification(
        "Error!",
        "Special characters are not allowed",
        "error"
      );
      return;
    } else if (regex.Script_Regex !== null && regex.Script_Regex.test(inputValue)) {
      // HTML Tags and Script detected, prevent it from being added
      showNotification(
        "Error!",
        "Submission fails. Input contains prohibited words. Please remove them and try again.",
        "error"
      );
      return;
    } else if (regex.Script_Regex === null || regex.Emoji_Regex === null) {
      showNotification(
        "Error!",
        "Validation configuration is missing. Please contact support.",
        "error"
      );
      return;
    }
    const validateFields = validateField(e, field, fields);
    setFields({ ...validateFields });
  }, []);

  const handleCountryChange = useCallback(
    (event) => {
      setDisableSubmit(true);
      let fieldsValue = fields;
      fieldsValue.ProductName.value = "";
      fieldsValue.ShowInList.value = "";
      fieldsValue.TETType.value = "";
      fieldsValue.TETProductName.value = "";
      const affiliate = countryList.find(
        (item) => item.country === event
      ).affiliate;

      const queryParams = {
        affiliate: affiliate,
      };

      api_v2.fetchTETProducts(queryParams, (res) => {
        const TETProductOptions = [];
        setProductList(res);
        res.forEach((product) => {
          TETProductOptions.push({
            value: product.productCode,
            label: product.name,
          });
        });
        setProductOptions(TETProductOptions);
      });
      setFields({ ...fieldsValue });
    },
    [countryList]
  );

  const handleProductChange = useCallback(
    (event) => {
      setDisableSubmit(true);
      const productName = productList.find(
        (item) => item.productCode === event
      ).name;

      setSelectedProductName(productName);
      const payload = {
        country: fields.country.value,
        productCode: event,
      };

      api_v2.fetchTETProductMap(payload, (res) => {
        if (res.length > 0) {
          let fieldsValue = fields;
          fieldsValue.TETProductName.value = res[0].stabilityProductName;
          setFields({ ...fieldsValue });
          setSelectedProductDetails(res[0]);
          setShowInTheList(res[0].showInTETList);
        } else {
          setShowAdminConfirmationModal(true);
        }
      });
    },
    [fields, productList]
  );

  const showNotification = (title, message, status) => {
    props.notify({
      title,
      message,
      status,
      position: "tr",
      dismissible: true,
      dismissAfter: 3000,
    });
  };

  const UpdateProductDetails = useCallback((payload) => {
    api_v2.updateTETProdMap(payload, (res) => {
      if (res.ok && res.status === 200) {
        resetState();
        showNotification(
          "Success",
          "Product is updated Successfully",
          "success"
        );
      } else {
        showNotification(
          "Error",
          "Something went wrong, Please try again later",
          "error"
        );
      }
    });
  }, []);

  const handleUpdateProductDetails = useCallback(() => {
    let fieldsValue = fields;
    const payload = {
      country: selectedProductDetails.country,
      productCode: selectedProductDetails.productCode,
      name: selectedProductDetails.name,
      stabilityProductName: fieldsValue.TETProductName.value,
      showInTETList: showInTheList,
    };
    UpdateProductDetails(payload);
  }, [fields, selectedProductDetails, showInTheList]);

  const handleCreateNewProductDetails = useCallback(() => {
    let fieldsValue = fields;
    const payload = {
      country: fieldsValue.country.value,
      productCode: fieldsValue.ProductName.value,
      name: selectedProductName,
      stabilityProductName: "",
      showInTETList: false,
    };
    UpdateProductDetails(payload);
  }, [selectedProductName, fields]);

  const handleCloseModal = () => {
    resetState();
    setShowAdminConfirmationModal(false);
  };

  return (
    <div id="manage-admin-list" className="v2_container">
      <div className="row">
        <AdminConfirmationModal
          isModalOpen={showAdminConfirmationModal}
          toggleYes={handleCreateNewProductDetails}
          toggleCancel={handleCloseModal}
          bodyContent={
            "This product not exist in our system. Are you sure do you want to configure?"
          }
        />
        <AdminNavigation className="col-xl-2 col-lg-3 col-md-4 col-6" />
        <div className="col-xl-10 col-lg-9 col-md-8 col-6 p-l-15">
          <div className="row manage-admin-list-header">
            <div className="col-12 col-xl-12">
              <h3 className="mb-3 header-primary">Manage TET Products</h3>
            </div>
          </div>
          <div>
            <div className="row">
              <div className=" col-md-4">
                <Select
                  searchable
                  clearable={false}
                  options={countryOptions}
                  field={fields.country}
                  validate={(e) => validate(e, fields.country)}
                  onChange={(e) => handleCountryChange(e)}
                />
              </div>
              {fields.country.value !== "" && (
                <div className="col-md-4">
                  <Select
                    searchable
                    clearable={false}
                    options={productOptions}
                    field={fields.ProductName}
                    validate={(e) => validate(e, fields.ProductName)}
                    onChange={(e) => handleProductChange(e)}
                  />
                </div>
              )}
            </div>
            {Object.keys(selectedProductDetails).length > 0 && (
              <div>
                <div className="row">
                  <div className=" col-md-4 mt-3">
                    <InputGroup
                      type="text"
                      searchable={false}
                      clearable={false}
                      field={fields.TETProductName}
                      validate={(e) => validate(e, fields.TETProductName)}
                      onChange={() => setDisableSubmit(false)}
                    />
                  </div>
                </div>
                <div
                  className="row"
                  style={{
                    alignItems: "end",
                    height: "50px",
                    marginBottom: "15px",
                  }}
                >
                  <div className="col-md-2">
                    <label className="custom-control custom-checkbox mr-0">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        checked={showInTheList}
                        onChange={() => {
                          setShowInTheList(!showInTheList);
                          setDisableSubmit(false);
                        }}
                      />
                      <span
                        id="enableTETProduct"
                        className="custom-control-indicator"
                      />
                      <span
                        className="custom-control-description"
                        style={{ marginLeft: "3px" }}
                      >
                        Show In List
                      </span>
                    </label>
                  </div>
                </div>
                <div>
                  <button
                    className="btn btn-lg btn-info btn-rounded save_button"
                    onClick={handleUpdateProductDetails}
                    disabled={disableSubmit}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-lg btn-info btn-rounded"
                    onClick={resetState}
                  >
                    cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  referenceData: state.referenceData,
});

export default connect(mapStateToProps, { notify })(TETProductManagement);



// WEBPACK FOOTER //
// ./src/mi-app/adminPanel/TET/V2TETProductManagement.js